import React from 'react';
import type { PlanWithAgePremiums } from '@/components/widgets/PlansSection';
import { Elements } from '@/components/elements';
import { formatCurrencyWithCommas, formatCopayAmount, formatVisitCopay } from '@/utils/formatNumber';
import LinkIcon from '@/assets/icons/NewTabIcon.svg';
import CheckIcon from '@/assets/icons/CheckIcon.svg';
import Board from '@/assets/icons/Board.svg';
import Image from 'next/image';

export interface ComparePlanCardProps {
    plan: PlanWithAgePremiums;
    familyAges?: string[];
    isStateCountrySearch?: boolean;
    employerContributionAmount?: number | null;
    onToggleCompare?: () => void;
    isCompared?: boolean;
    comparePosition?: number;
    compareTotal?: number;
}

export const ComparePlanCard: React.FC<ComparePlanCardProps> = ({
    plan,
    familyAges = [],
    isStateCountrySearch = false,
    employerContributionAmount = null,
    onToggleCompare,
    isCompared,
    comparePosition,
    compareTotal,
}) => {
    const age18To65Label =
        isStateCountrySearch && (plan.premiumAge18 || plan.premiumAge65) ? (
            <span className="shrink-0 text-sm text-gray-900" aria-hidden>
                to
            </span>
        ) : null;

    const isFamilyPlan = familyAges.length > 1;
    const isCurrentPlan = (plan as any).isCurrentPlan === true;
    const toTitleCase = (input?: string) => {
        const s = (input ?? '').trim();
        if (!s) return '';
        return s
            .replace(/\s+/g, ' ')
            .split(' ')
            .map((word) => {
                const lower = word.toLowerCase();
                return lower ? lower[0].toUpperCase() + lower.slice(1) : lower;
            })
            .join(' ');
    };
    const identifierValue =
        plan.identifiers?.find((id) => id.type === 'hios_id')?.value ?? plan.identifiers?.[0]?.value;
    const hiosId = identifierValue && identifierValue.length > 5 ? identifierValue : plan.id;

    const deductible = isFamilyPlan
        ? parseFloat(plan.family_medical_deductible?.replace(/[^0-9.]/g, '') || '0')
        : plan.deductible_numerical;

    const maxOutOfPocket = isFamilyPlan
        ? parseFloat(plan.family_medical_moop?.replace(/[^0-9.]/g, '') || '0')
        : plan.max_out_of_pocket_numerical;

    const premiumStr = plan.premium != null ? String(plan.premium) : '';
    const premiumNum = parseFloat(premiumStr.replace(/[^0-9.]/g, '')) || 0;
    const hasEmployerContribution = premiumNum > 0 && employerContributionAmount != null && employerContributionAmount > 0;
    const employerPay = hasEmployerContribution ? Math.min(employerContributionAmount!, premiumNum) : 0;
    const employeePay = hasEmployerContribution ? Math.max(premiumNum - employerContributionAmount!, 0) : 0;
    // `available` is intentionally not shown in compare cards (label is shortened), but kept in PlanCard.

    if (isCurrentPlan) {
        const carrierName = plan.carrier?.name_long || plan.carrier?.name || '';
        const displayName = plan.display_name || plan.name || '';
        return (
            <>
            <article className="rounded-2xl shadow-sm border border-brand-700 bg-white w-[365px] h-full mx-auto flex flex-col">
                {/* Header: fixed 112px, just the plan name (no label) */}
                <div className="flex h-[112px] flex-row items-center justify-between gap-3 p-2 sm:p-2 bg-gray-50 rounded-t-2xl">
                    <div className="w-full min-w-0">
                        <div className="flex flex-col min-w-0 gap-1">
                            <div
                                className="text-base font-medium text-gray-900 line-clamp-1"
                                title={toTitleCase(carrierName)}
                            >
                                {toTitleCase(carrierName)}
                            </div>
                        </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                        <Elements.ParsePlanType type={plan.level} />
                    </div>
                </div>

                <div className="bg-blue-50 rounded-b-2xl p-4 flex flex-col flex-1">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col">
                            <div className="text-sm font-normal text-gray-450">Plan Name</div>
                            <div
                                className="text-base font-medium text-gray-900 wrap-break-word"
                                title={toTitleCase(displayName)}
                            >
                                {toTitleCase(displayName)}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col">
                                <div className="text-sm font-normal text-gray-500">Deductible</div>
                                <div className="text-base font-medium text-gray-900">
                                    {deductible ? `${formatCurrencyWithCommas(deductible)}/yr` : '—'}
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <div className="text-sm font-normal text-gray-500">Max–Out–Of–Pocket</div>
                                <div className="text-base font-medium text-gray-900">
                                    {maxOutOfPocket ? `${formatCurrencyWithCommas(maxOutOfPocket)}/yr` : '—'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Keep monthly premium at the bottom for consistency */}
                    <div className="mt-auto pt-4">
                        {isStateCountrySearch && (plan.premiumAge18 || plan.premiumAge65) ? (
                            <div className="w-full rounded-2xl mt-2">
                                <div className="flex w-full items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="relative flex h-[56px] md:h-[56px] lg:h-[38px] w-fit flex-col items-start justify-center gap-0 md:gap-1 rounded-lg bg-white px-3 lg:flex-row lg:items-center lg:gap-2">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask:
                                                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative text-sm font-normal text-gray-500 whitespace-nowrap">
                                                Age 18
                                            </span>
                                            <span className="relative text-lg font-medium text-primary-500 leading-none whitespace-nowrap">
                                                {plan.premiumAge18 ? formatCurrencyWithCommas(parseFloat(plan.premiumAge18)) : '—'}
                                            </span>
                                        </div>

                                        {age18To65Label}

                                        <div className="relative flex h-[56px] md:h-[56px] lg:h-[38px] w-fit flex-col items-start justify-center gap-0 md:gap-1 rounded-lg bg-white px-3 lg:flex-row lg:items-center lg:gap-2">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask:
                                                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative text-sm font-normal text-gray-500 whitespace-nowrap">
                                                Age 65
                                            </span>
                                            <span className="relative text-lg font-medium text-primary-500 leading-none whitespace-nowrap">
                                                {plan.premiumAge65 ? formatCurrencyWithCommas(parseFloat(plan.premiumAge65)) : '—'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="ml-auto flex lg:hidden shrink-0 flex-col items-end text-base font-medium text-gray-800 leading-tight text-right">
                                        <span className="whitespace-nowrap">Monthly</span>
                                        <span className="whitespace-nowrap">Premium</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="w-full rounded-2xl mt-2">
                                {hasEmployerContribution ? (
                                    <div className="flex w-full flex-row flex-nowrap items-start justify-start gap-2">
                                        {/* Premium */}
                                        <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                            <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white">
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        padding: 1,
                                                        borderRadius: 8,
                                                        background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                        WebkitMaskComposite: 'xor',
                                                        maskComposite: 'exclude',
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                                <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-primary-500 sm:text-base tabular-nums">
                                                    {formatCurrencyWithCommas(premiumNum)}
                                                </span>
                                            </div>
                                            <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                                Premium
                                            </div>
                                        </div>

                                        <div className="h-[43px] flex items-center">
                                            <span className="text-base font-medium text-gray-700">-</span>
                                        </div>

                                        {/* Employer */}
                                        <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                            <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-brand-100">
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        padding: 1,
                                                        borderRadius: 8,
                                                        background: 'transparent',
                                                        WebkitMask:
                                                            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                        WebkitMaskComposite: 'xor',
                                                        maskComposite: 'exclude',
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                                <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-gray-900 sm:text-base tabular-nums">
                                                    {formatCurrencyWithCommas(employerPay)}
                                                </span>
                                            </div>
                                            <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                                Employer
                                            </div>
                                        </div>

                                        <div className="h-[43px] flex items-center">
                                            <span className="text-base font-medium text-gray-700">=</span>
                                        </div>

                                        {/* Employee */}
                                        <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                            <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white border border-brand-100">
                                                <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-gray-900 sm:text-base tabular-nums">
                                                    {formatCurrencyWithCommas(employeePay)}
                                                </span>
                                            </div>
                                            <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                                Employee
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Single premium: MoneyBox should match the label width.
                                    <div className="flex w-fit flex-col items-stretch">
                                        <div className="relative flex h-[43px] w-full items-center justify-center px-4 rounded-lg bg-white">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative w-full min-w-0 truncate text-center text-base font-medium text-primary-500 sm:text-lg tabular-nums">
                                                {premiumNum > 0 ? formatCurrencyWithCommas(premiumNum) : '—'}
                                            </span>
                                        </div>
                                        <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900 whitespace-nowrap">
                                            {familyAges.length > 1 ? 'Family Monthly Premium' : 'Monthly Premium'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </article>
            </>
        );
    }

    return (
        <>
        <article className="rounded-2xl shadow-sm border border-brand-700 bg-white w-[365px] h-full mx-auto flex flex-col">
            <div
                className={`flex flex-row items-center justify-between gap-3 p-2 sm:p-2 ${isCurrentPlan ? 'h-[112px]' : ''
                    }`}
            >
                <div className="w-full min-w-0">
                    <div className="flex w-full flex-row items-center gap-3">
                        {isCurrentPlan ? (
                            <div className="min-w-0 self-center">
                                <div className="text-sm font-normal text-gray-500">Plan Name
                                </div>
                                <div className="text-base font-medium text-gray-900 wrap-break-word line-clamp-2">
                                    {plan.display_name}
                                </div>
                            </div>
                        ) : plan.carrier.logo_url ? (
                            <img
                                src={plan.carrier.logo_url}
                                alt="logo"
                                className="size-24 shrink-0 rounded object-contain"
                                loading="lazy"
                                decoding="async"
                            />
                        ) : null}
                    </div>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                    <Elements.ParsePlanType type={plan.level} />
                    <Elements.ParseExchange on_market={plan.on_market} off_market={plan.off_market} />
                </div>
            </div>

            <div className="bg-blue-50 rounded-b-2xl p-2 sm:p-2 md:p-2 flex flex-col flex-1">
                <div>
                    {/* Мобильный layout PlanSummary */}
                    <div className="mb-1 w-full overflow-hidden">
                        <div className="flex flex-col items-start gap-2 w-full overflow-hidden">
                            <div className="flex w-full flex-col min-w-0 overflow-hidden">
                                <div className="flex w-full items-center justify-between gap-2">
                                    <div className="text-sm font-normal text-gray-450">Plan Name</div>
                                    <div className="text-sm font-normal text-gray-450 whitespace-nowrap">
                                        HIOS ID: <span className="font-medium text-gray-450">{hiosId}</span>
                                    </div>
                                </div>
                                <div className="text-base font-medium text-gray-900 wrap-break-word">
                                    {plan.display_name}
                                </div>
                            </div>
                            <div className="flex justify-between flex-row w-full gap-2">
                                <div className="flex flex-col shrink-0 min-w-0 overflow-hidden">
                                    <div className="text-sm font-normal text-gray-500">Type</div>
                                    <div className="text-base font-medium text-gray-900 wrap-break-word">
                                        {plan.plan_type}
                                    </div>
                                </div>
                                <div className="flex flex-col shrink-0 min-w-0 overflow-hidden">
                                    <div className="text-sm font-normal text-gray-500">Deductible</div>
                                    <div className="text-base font-medium text-gray-900 wrap-break-word">
                                        {formatCurrencyWithCommas(deductible)}/yr
                                    </div>
                                </div>
                                <div className="flex flex-col shrink-0 min-w-0 overflow-hidden">
                                    <div className="text-sm font-normal text-gray-500">Max–Out–Of–Pocket</div>
                                    <div className="text-base font-medium text-gray-900 wrap-break-word">
                                        {formatCurrencyWithCommas(maxOutOfPocket)}/yr
                                    </div>
                                </div>
                            </div>

                            {/* HSA Eligible & Specialist Without Referral */}
                            <div className="flex w-full flex-row gap-2 mt-3">
                                {plan.hsa_eligible && (
                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                        <Image src={CheckIcon} alt="" width={20} height={20} />
                                        <span className="text-sm">HSA Eligible</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                    {!plan.gated ? (
                                        <Image src={CheckIcon} alt="" width={20} height={20} />
                                    ) : (
                                        <Image src={Board} alt="Board" width={16} height={16} />
                                    )}
                                    <span
                                        className="text-sm"
                                        style={
                                            plan.gated
                                                ? {
                                                    textDecoration: 'line-through',
                                                    textDecorationThickness: '1px',
                                                    textDecorationColor: '#6B7280',
                                                }
                                                : {}
                                        }
                                    >
                                        Specialist Without Referral
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* VisitCopays - мобильный layout */}
                    <div className="mb-[7px] w-full">
                        <div className="grid grid-cols-3 gap-0 w-full">
                            <div className="flex flex-col p-1">
                                <div className="text-sm font-normal text-gray-500">Preventative</div>
                                <div className="text-base font-medium text-gray-900">
                                    {formatCopayAmount(plan.preventitive_numerical)}
                                </div>
                            </div>
                            <div className="flex flex-col p-1">
                                <div className="text-sm font-normal text-gray-500">Primary Care</div>
                                <div className="text-base font-medium text-gray-900">
                                    {formatCopayAmount(plan.primary_care_numerical)}
                                </div>
                            </div>
                            <div className="flex flex-col p-1">
                                <div className="text-sm font-normal text-gray-500">Urgent Care</div>
                                <div className="text-base font-medium text-gray-900">
                                    {formatCopayAmount(plan.urgent_care_numerical)}
                                </div>
                            </div>
                            <div className="flex flex-col p-1">
                                <div className="text-sm font-normal text-gray-500">Emergency</div>
                                <div className="text-base font-medium text-gray-900">
                                    {formatVisitCopay(plan.emergency_numerical, plan.emergency_room)}
                                </div>
                            </div>
                            <div className="flex flex-col p-1">
                                <div className="text-sm font-normal text-gray-500">Specialist</div>
                                <div className="text-base font-medium text-gray-900">
                                    {formatCopayAmount(plan.specelist_numerical)}
                                </div>
                            </div>
                            <div className="flex flex-col p-1">
                                <div className="text-sm font-normal text-gray-500">Telemedicine</div>
                                <div className="text-base font-medium text-gray-900">
                                    {(() => {
                                        if (!plan.telemedicine) return '—';
                                        const value = plan.telemedicine.replace('$', '').toLowerCase();
                                        if (value === 'true') return 'Yes';
                                        if (value === 'false') return 'No';
                                        return plan.telemedicine;
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Links (как в мобильной PlanCard) */}
                    <div className="w-full md:col-span-3">
                        {(() => {
                            const links = [
                                plan.benefits_summary_url && { url: plan.benefits_summary_url, text: 'Summary of Benefits (SBC)' },
                                plan.network_provider_directory_url && { url: plan.network_provider_directory_url, text: 'Find In-Network Providers' },
                                plan.drug_formulary_url && { url: plan.drug_formulary_url, text: 'Rx Drug Formulary' },
                            ].filter(Boolean) as Array<{ url: string; text: string }>;

                            if (links.length === 0) return null;

                            return (
                                <div className="mt-[7px] flex w-full flex-col items-start gap-[7px]">
                                    {links.length > 0 && (
                                        <div className="flex w-full flex-row flex-wrap items-center gap-4">
                                            {plan.benefits_summary_url && (
                                                <a
                                                    className="flex shrink-0 gap-1 text-base text-primary hover:underline"
                                                    href={plan.benefits_summary_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    SBC <Image src={LinkIcon} alt="Link" width={16} height={16} />
                                                </a>
                                            )}
                                            {plan.network_provider_directory_url && (
                                                <a
                                                    className="flex shrink-0 gap-1 text-base text-primary hover:underline"
                                                    href={plan.network_provider_directory_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Providers <Image src={LinkIcon} alt="Link" width={16} height={16} />
                                                </a>
                                            )}
                                            {plan.drug_formulary_url && (
                                                <a
                                                    className="flex shrink-0 gap-1 text-base text-primary hover:underline"
                                                    href={plan.drug_formulary_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Rx Formulary <Image src={LinkIcon} alt="Link" width={16} height={16} />
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    {/* HIOS ID is shown next to the "Plan Name" label above */}
                                </div>
                            );
                        })()}
                    </div>

                </div>

                {/* Monthly Premium всегда внизу – инлайн реализация */}
                <div className="mt-auto">
                    {isStateCountrySearch && (plan.premiumAge18 || plan.premiumAge65) ? (
                        <div className="w-full rounded-2xl mt-2">
                            <div className="flex w-full items-center">
                                <div className="flex items-center gap-3">
                                    {/* Age 18 */}
                                    <div className="relative flex h-[56px] w-fit flex-col items-start justify-center gap-0 rounded-lg bg-white px-3">
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                padding: 1,
                                                borderRadius: 8,
                                                background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                WebkitMask:
                                                    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                WebkitMaskComposite: 'xor',
                                                maskComposite: 'exclude',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        <span className="relative text-sm font-normal text-gray-500 whitespace-nowrap">
                                            Age 18
                                        </span>
                                        <span className="relative text-lg font-medium text-primary-500 leading-none whitespace-nowrap">
                                            {plan.premiumAge18
                                                ? `${formatCurrencyWithCommas(
                                                    parseFloat(String(plan.premiumAge18).replace(/[^0-9.]/g, '')) || 0
                                                )}`
                                                : '—'}
                                        </span>
                                    </div>

                                    {age18To65Label}

                                    {/* Age 65 */}
                                    <div className="relative flex h-[56px] w-fit flex-col items-start justify-center gap-0 rounded-lg bg-white px-3">
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                padding: 1,
                                                borderRadius: 8,
                                                background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                WebkitMask:
                                                    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                WebkitMaskComposite: 'xor',
                                                maskComposite: 'exclude',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        <span className="relative text-sm font-normal text-gray-500 whitespace-nowrap">
                                            Age 65
                                        </span>
                                        <span className="relative text-lg font-medium text-primary-500 leading-none whitespace-nowrap">
                                            {plan.premiumAge65
                                                ? `${formatCurrencyWithCommas(
                                                    parseFloat(String(plan.premiumAge65).replace(/[^0-9.]/g, '')) || 0
                                                )}`
                                                : '—'}
                                        </span>
                                    </div>
                                </div>

                                <div className="ml-auto flex shrink-0 flex-col items-end text-base font-medium text-gray-800 leading-tight text-right">
                                    <span className="whitespace-nowrap">Monthly</span>
                                    <span className="whitespace-nowrap">Premium</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full rounded-2xl mt-2">
                            {hasEmployerContribution ? (
                                <div className="flex w-full flex-row flex-nowrap items-start justify-start gap-2">
                                    {/* Monthly Premium */}
                                    <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                        <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-primary-500 sm:text-base tabular-nums">
                                                {formatCurrencyWithCommas(premiumNum)}
                                            </span>
                                        </div>
                                        <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                            Premium
                                        </div>
                                    </div>

                                    <div className="h-[43px] flex items-center">
                                        <span className="text-base font-medium text-gray-700">-</span>
                                    </div>

                                    {/* Employer */}
                                    <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                        <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-brand-100">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'transparent',
                                                    WebkitMask:
                                                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-gray-900 sm:text-base tabular-nums">
                                                {formatCurrencyWithCommas(employerPay)}
                                            </span>
                                        </div>
                                        <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                            Employer
                                        </div>
                                    </div>

                                    <div className="h-[43px] flex items-center">
                                        <span className="text-base font-medium text-gray-700">=</span>
                                    </div>

                                    {/* Employee */}
                                    <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                        <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white border border-brand-100">
                                            <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-gray-900 sm:text-base tabular-nums">
                                                {formatCurrencyWithCommas(employeePay)}
                                            </span>
                                        </div>
                                        <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                            Employee
                                        </div>
                                    </div>

                                </div>
                            ) : (
                                <div className="flex flex-col items-start justify-start gap-3 sm:flex-row sm:items-start sm:justify-start sm:gap-4">
                                    <div className="relative flex w-full flex-1 flex-row items-center gap-2 md:gap-1 lg:gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-5">
                                        <div className="relative flex flex-1 h-[35px] items-center justify-center px-4 sm:px-4 md:px-4 rounded-lg bg-white md:w-auto lg:w-[150px] sm:w-[150px] sm:flex-none">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask:
                                                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative text-center text-base font-medium text-primary-500 sm:text-lg">
                                                {plan.premium ? formatCurrencyWithCommas(parseFloat(plan.premium)) : '—'}
                                            </span>
                                        </div>
                                        <span className="text-left md:text-left text-base font-medium text-gray-800 sm:text-base md:text-base lg:text-base shrink-0">
                                            {familyAges.length > 1 ? (
                                                <span className="md:block lg:inline">
                                                    <span className="md:block lg:inline">Family</span>{' '}
                                                    <span className="md:block lg:inline">Monthly Premium</span>
                                                </span>
                                            ) : (
                                                <span className="md:block lg:inline">
                                                    <span className="md:block lg:inline">Monthly</span>{' '}
                                                    <span className="md:block lg:inline">Premium</span>
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </article>
        </>
    );
};

